import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { LanguageToggle } from "@/components/LanguageToggle"
import {
  Upload,
  Bot,
  LineChart,
  Shield,
  Clock,
  CheckCircle,
  MessageSquare,
  Sparkles,
  ArrowRight,
  Users,
  Brain,
  Database,
  Smartphone,
  BarChart3,
} from "lucide-react"

export function LandingPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const features = [
    {
      icon: Upload,
      title: t("landing.features.uploadTitle"),
      description: t("landing.features.uploadDesc"),
    },
    {
      icon: Bot,
      title: t("landing.features.aiLearnsTitle"),
      description: t("landing.features.aiLearnsDesc"),
    },
    {
      icon: MessageSquare,
      title: t("landing.features.startChattingTitle"),
      description: t("landing.features.startChattingDesc"),
    },
  ]

  const industries = [
    { name: t("landing.industries.ecommerce"), icon: "🛒", description: t("landing.industries.ecommerceDesc") },
    { name: t("landing.industries.saas"), icon: "💻", description: t("landing.industries.saasDesc") },
    { name: t("landing.industries.healthcare"), icon: "🏥", description: t("landing.industries.healthcareDesc") },
    { name: t("landing.industries.financial"), icon: "💰", description: t("landing.industries.financialDesc") },
    { name: t("landing.industries.education"), icon: "🎓", description: t("landing.industries.educationDesc") },
  ]

  const benefits = [
    t("landing.benefits.noSignup"),
    t("landing.benefits.freeTry"),
    t("landing.benefits.dataSecure"),
    t("landing.benefits.twoMinutes"),
  ]

  const stats = [
    { num: t("landing.stats.concurrentNum"), label: t("landing.stats.concurrentLabel") },
    { num: t("landing.stats.resolutionNum"), label: t("landing.stats.resolutionLabel") },
    { num: t("landing.stats.responseNum"), label: t("landing.stats.responseLabel") },
    { num: t("landing.stats.availabilityNum"), label: t("landing.stats.availabilityLabel") },
  ]

  const features6 = [
    {
      icon: Users,
      title: t("landing.features6.f1Title"),
      description: t("landing.features6.f1Desc"),
    },
    {
      icon: Brain,
      title: t("landing.features6.f2Title"),
      description: t("landing.features6.f2Desc"),
    },
    {
      icon: Database,
      title: t("landing.features6.f3Title"),
      description: t("landing.features6.f3Desc"),
    },
    {
      icon: MessageSquare,
      title: t("landing.features6.f4Title"),
      description: t("landing.features6.f4Desc"),
    },
    {
      icon: Smartphone,
      title: t("landing.features6.f5Title"),
      description: t("landing.features6.f5Desc"),
    },
    {
      icon: BarChart3,
      title: t("landing.features6.f6Title"),
      description: t("landing.features6.f6Desc"),
    },
  ]

  const testimonials = [
    {
      text: t("landing.testimonials.t1Text"),
      author: t("landing.testimonials.t1Author"),
      role: t("landing.testimonials.t1Role"),
    },
    {
      text: t("landing.testimonials.t2Text"),
      author: t("landing.testimonials.t2Author"),
      role: t("landing.testimonials.t2Role"),
    },
    {
      text: t("landing.testimonials.t3Text"),
      author: t("landing.testimonials.t3Author"),
      role: t("landing.testimonials.t3Role"),
    },
  ]

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-foreground">{t("nav.aiSupportDemo")}</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#how-it-works" className="text-muted hover:text-foreground transition-colors">
                {t("nav.howItWorks")}
              </a>
              <a href="#industries" className="text-muted hover:text-foreground transition-colors">
                {t("nav.industries")}
              </a>
              <Button onClick={() => navigate("/demo/select")}>
                {t("nav.startFreeDemo")}
              </Button>
              <LanguageToggle />
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden">
        <div className="absolute inset-0 gradient-hero opacity-5" />
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-secondary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-primary/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-8">
              <Sparkles className="w-4 h-4" />
              <span>AI-Powered Customer Service</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
              {t("landing.heroTitle")}
            </h1>

            <p className="text-lg md:text-xl text-muted max-w-2xl mx-auto mb-10">
              {t("landing.heroSubtitle")}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <Button
                size="lg"
                onClick={() => navigate("/demo/select")}
                className="w-full sm:w-auto text-lg px-8"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                {t("landing.startFreeDemo")}
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="w-full sm:w-auto text-lg px-8"
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              >
                {t("landing.learnMore")}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-success" />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-12 bg-white border-y">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index} className="flex flex-col items-center">
                <span className="text-4xl font-bold text-primary mb-2">{stat.num}</span>
                <span className="text-sm text-muted">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t("landing.howItWorksTitle")}
            </h2>
            <p className="text-lg text-muted max-w-2xl mx-auto">
              {t("landing.howItWorksSubtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="relative p-8 rounded-2xl bg-background border hover-lift"
              >
                <div className="absolute -top-4 left-8 w-8 h-8 rounded-full gradient-primary flex items-center justify-center text-white font-bold text-sm">
                  {index + 1}
                </div>
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">
                  {feature.title}
                </h3>
                <p className="text-muted">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features 6-grid */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              {t("landing.features6Tag")}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t("landing.features6Title")}
            </h2>
            <p className="text-lg text-muted max-w-2xl mx-auto">
              {t("landing.features6Subtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features6.map((feature, index) => (
              <div
                key={index}
                className="p-6 rounded-2xl bg-white border hover-lift"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {feature.title}
                </h3>
                <p className="text-muted text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section id="industries" className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t("landing.industryTemplatesTitle")}
            </h2>
            <p className="text-lg text-muted max-w-2xl mx-auto">
              {t("landing.industryTemplatesSubtitle")}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {industries.map((industry, index) => (
              <div
                key={index}
                className="p-6 rounded-xl bg-white border text-center hover-lift cursor-pointer"
                onClick={() => navigate("/demo/select")}
              >
                <div className="text-4xl mb-4">{industry.icon}</div>
                <h3 className="font-semibold text-foreground mb-2">{industry.name}</h3>
                <p className="text-sm text-muted">{industry.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-16 bg-white border-y">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="flex flex-col items-center">
              <Shield className="w-10 h-10 text-success mb-4" />
              <h3 className="font-semibold text-foreground mb-2">{t("landing.trust.secureTitle")}</h3>
              <p className="text-sm text-muted">{t("landing.trust.secureDesc")}</p>
            </div>
            <div className="flex flex-col items-center">
              <Clock className="w-10 h-10 text-secondary mb-4" />
              <h3 className="font-semibold text-foreground mb-2">{t("landing.trust.instantTitle")}</h3>
              <p className="text-sm text-muted">{t("landing.trust.instantDesc")}</p>
            </div>
            <div className="flex flex-col items-center">
              <LineChart className="w-10 h-10 text-accent mb-4" />
              <h3 className="font-semibold text-foreground mb-2">{t("landing.trust.gptTitle")}</h3>
              <p className="text-sm text-muted">{t("landing.trust.gptDesc")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              {t("landing.testimonialsTag")}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t("landing.testimonialsTitle")}
            </h2>
            <p className="text-lg text-muted max-w-2xl mx-auto">
              {t("landing.testimonialsSubtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-background border rounded-2xl p-6 hover-lift"
              >
                <div className="text-5xl font-serif text-primary/30 leading-none mb-4">"</div>
                <p className="text-muted text-sm leading-relaxed mb-6">
                  {testimonial.text}
                </p>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                    {testimonial.author.charAt(0)}
                  </div>
                  <div>
                    <div className="font-semibold text-foreground text-sm">{testimonial.author}</div>
                    <div className="text-xs text-muted">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary to-primary-dark">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t("landing.ctaTitle")}
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            {t("landing.ctaSubtitle")}
          </p>
          <Button
            size="lg"
            variant="secondary"
            onClick={() => navigate("/demo/select")}
            className="text-lg px-8"
          >
            {t("landing.ctaButton")}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-foreground text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl">{t("nav.aiSupportDemo")}</span>
            </div>
            <div className="text-sm text-white/60">
              © 2026 {t("landing.footer")}
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
